import {
	AppEnumHelper,
	dateCompare,
	DetailItem,
	DetailSection,
	DocumentEnums,
	DOMAIN_TYPES,
} from "@/Shared";

import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { DocumentModel } from "../DocumentModel";
import { MediaFileViewModel } from "@/Domains/MediaFile";
import { AccessRightModel } from "@/Domains/Auth";

/**
 * =====================================================
 * Interface: DocumentVMParams
 * =====================================================
 *
 * Parameters required to instantiate a DocumentViewModel:
 * - document: The document model to wrap
 * - viewEnums: Application enums for display labels
 * - accessRight: User access rights for controlling visibility
 * - isDisplaying: Boolean controlling UI state (active/inactive card)
 */
export interface DocumentVMParams {
	 document: DocumentModel,
     viewEnums: DocumentEnums;
	 accessRight : AccessRightModel;
	 isDisplaying : boolean
}

/**
 * =====================================================
 * Class: DocumentViewModel
 * =====================================================
 *
 * Description:
 * Encapsulates the presentation logic for a Document.
 * Provides formatted fields, computed labels, audit info, and detail sections
 * for rendering in the UI.
 *
 * Responsibilities:
 * - Expose document data in a UI-friendly format
 * - Compute human-readable labels for category, type, and resource
 * - Wrap audit information and media view models
 * - Provide sections for detailed display, including audit trail if permitted
 * - Support sorting via compareTo method
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export class DocumentViewModel {
	public id: number;
	public model : DocumentModel;
	public documentTitle: string;
	public documentType: string;
	public documentCategory: string;
	public parameters: Record<string, any> | null;
	public documentSource: string | null;
	public template: string | null;
	public resourceId: number;

	public audit!: AuditViewModel;
	public creationDate!: string;
	public creatorName!: string;
	public mediaView! : MediaFileViewModel;

	public isDeleted!: boolean;
    public cardClass!: string;
	public cardHeaderTitle!: string;


	/**
	 * Constructor
	 *
	 * @param params - DocumentVMParams
	 *
	 * Behavior:
	 * - Initializes properties from the document model
	 * - Builds audit and media sub-models
	 * - Computes display labels from enums
	 * - Determines card UI state
	 */
	constructor(
		protected params : DocumentVMParams
	) {
		const { document, viewEnums,  isDisplaying } = this.params;
		this.model = document;
		this.audit = new AuditViewModel(document as AuditModel);
		this.id = document?.id ?? 0;
		this.parameters = document?.parameters ?? null;
		this.documentSource = document?.documentSource ?? "";
		this.template = document?.template ?? "";

		this.documentTitle = document.documentTitle ?? "";
		
		this.resourceId = document?.resourceId ?? 0;

		this.documentType = AppEnumHelper.getLabel(viewEnums.DocumentTypeEnum, document?.documentType);
		this.documentCategory = AppEnumHelper.getLabel(viewEnums.DocumentCategoryEnum, document?.documentCategory);

		this.creationDate = this.audit.creationDate;
		this.creatorName = this.audit.creatorName;

		this.mediaView = new MediaFileViewModel(document.media, this.audit);

		this.isDeleted = !!document.deletedAt
        this.cardClass = isDisplaying ? 'active' : '';
		this.cardHeaderTitle = this.params.document.documentTitle;

	}

	/** =====================================================
	 * Title for UI Card
	 * =====================================================
	 */
	get title() {
		return  `N° ${this.id} - ${this.params.document.documentTitle}`;
	};

	/** =====================================================
	 * Detail Sections
	 * =====================================================
	 *
	 * Returns all sections to display in the detail view
	 */
	getAllSections = (): DetailSection[] => {
		const sections: DetailSection[] =  [
			{
				sectionFn: this.getDocumentSection,
				legend: "Détails du Document",
				type: "box",
			},
			{
				sectionFn: this.mediaView.getFileSection,
				legend: "Détails du Fichier",
				type: "box",
			},
		];
		if(this.params.accessRight.canAudit(DOMAIN_TYPES.Document)) {
			sections.push({
				sectionFn: this.audit.getAuditTrail,
				legend: "Traçabilité",
				type: "box",
			});
		}
		return sections;
	};

	/** =====================================================
	 * Document Section Details
	 * =====================================================
	 *
	 * Returns a list of label/value pairs representing the document
	 * for rendering in the UI
	 */
	getDocumentSection = () : DetailItem[] => {
         return [
			{ label: "N°", value: String(this.id) },
			{ label: "Titre", value: this.documentTitle },
			{ label: "N° Resource", value: String(this.resourceId) },
			{ label: "Type", value: this.documentType },
			{ label: "Catégorie", value: this.documentCategory },
			{ label: "Source", value: this.documentSource ?? "" },
			{ label: "Créé le", value: this.audit?.creationDate ?? "" },
			{ label: "Créé par", value: this.audit?.creatorName ?? "" },
		];
	}
	
	/** =====================================================
	 * Comparison Method
	 * =====================================================
	 *
	 * Compares this document to another by creation date for sorting
	 */
	compareTo(other: DocumentViewModel): number {
		return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
	}
}
